/**
 * Клиент API v2 школы — единый источник правды по аккаунтам экосистемы.
 *
 * Почему отдельный модуль, а не правка client.js. У v1 и v2 разные базовые
 * адреса и разные формы ответов, а переезд идёт постепенно: часть данных
 * (новости, идеи, тикеты, лента игр) в v2 ещё не существует и продолжает
 * жить в v1. Держать оба клиента рядом честнее, чем ветвить один по флагам.
 *
 * ⚠️ Адрес АБСОЛЮТНЫЙ. Путь `/api/v2/...` проксируется только на домене
 * школы; на rublox.pro он попадает в SPA-fallback и возвращает HTML —
 * ровно та ошибка, из-за которой аватары отдавали «Unexpected token '<'».
 *
 * Токен шлём в том же виде, что и v1 — СЫРЫМ, без префикса Bearer.
 * Проверено на живом authgate: он срезает "Bearer " опционально, поэтому
 * оба формата дают 200. Это важно: плеер и студия читают тот же токен из
 * localStorage['Authorization'], и менять его формат нельзя.
 */

import { getToken, refreshOnce, getRefreshToken, ApiError } from "./client";

export const V2_BASE = "https://minecraftia-school.ru/api/v2";

/**
 * Запрос к v2. Повторяет логику client.request: при 401 обновляет токен
 * и пробует ещё раз ровно один раз.
 */
export async function v2(path, options = {}) {
  const {
    method = "GET",
    body,
    auth = true,
    signal,
    _retried = false,
  } = options;

  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = token;
  }

  let res;
  try {
    res = await fetch(V2_BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw new ApiError("Не удалось связаться с сервером. Проверьте интернет.", 0);
  }

  if (res.status === 401 && auth && !_retried && getRefreshToken()) {
    const ok = await refreshOnce();
    if (ok) return v2(path, { ...options, _retried: true });
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (!res.ok) {
    // FastAPI кладёт человеческое сообщение в detail.
    const msg =
      (data && (data.detail || data.message)) || defaultMessage(res.status);
    throw new ApiError(typeof msg === "string" ? msg : "Ошибка запроса", res.status);
  }
  return data;
}

function defaultMessage(status) {
  switch (status) {
    case 400: return "Неверный запрос. Проверьте данные.";
    case 401: return "Сессия истекла. Войди заново.";
    case 403: return "Доступ запрещён.";
    case 404: return "Не найдено.";
    case 429: return "Слишком много запросов. Подожди немного.";
    case 503: return "Сервис временно недоступен. Попробуй ещё раз.";
    default:  return "Произошла ошибка. Попробуйте ещё раз.";
  }
}

/* ── профиль ──────────────────────────────────────────────────────── */

/**
 * Текущий пользователь.
 *
 * Отдаёт UUID в `id` — это и есть тот самый переход на v2. Числовой
 * v1-id наружу больше не светится: сведением занимается бэкенд
 * (см. identity_map в rublox-backend).
 */
export async function getMe(signal) {
  return v2("/identity/me", { signal });
}

/**
 * Чужой профиль по UUID аккаунта.
 *
 * ⚠️ Принимает ТОЛЬКО UUID. Числовой v1-id и ник дают «Пользователь
 * не найден» — проверено на боевом API. Для старых ссылок вида
 * /app/profile/1 сначала нужен resolveAccountId() из identityV2.
 */
export async function getUser(accountId, signal) {
  return v2(`/identity/users/${encodeURIComponent(accountId)}`, { signal });
}

/* ── кошелёк ──────────────────────────────────────────────────────── */

/** Балансы одним запросом: {diamond, star, rating}. */
export async function getBalances(signal) {
  return v2("/wallet/balances", { signal });
}

/** Только алмазы — частый случай, чтобы не тащить весь объект. */
export async function getDiamonds(signal) {
  const b = await getBalances(signal);
  return typeof b?.diamond === "number" ? b.diamond : 0;
}

/* ── косметика ────────────────────────────────────────────────────── */

/** Темы: {owned:[id], active:id, catalog:[{id,name,rarity,price}]}. */
export async function getThemes(signal) {
  return v2("/customization/themes", { signal });
}

export async function buyTheme(themeId) {
  return v2("/customization/themes/buy", {
    method: "POST",
    body: { theme_id: themeId },
  });
}

export async function activateTheme(themeId) {
  return v2("/customization/themes/activate", {
    method: "POST",
    body: { theme_id: themeId },
  });
}

/** Рамки аватара. Набор совпадает с Рублоксом 1:1 — портирован из Маськи. */
export async function getFrames(signal) {
  return v2("/customization/frames", { signal });
}

export async function buyFrame(frameId) {
  return v2("/customization/frames/buy", {
    method: "POST",
    body: { frame_id: frameId },
  });
}

export async function activateFrame(frameId) {
  return v2("/customization/frames/activate", {
    method: "POST",
    body: { frame_id: frameId },
  });
}

/** Цвета имени. "default" — сброс. */
export async function getNameColors(signal) {
  return v2("/customization/name-colors", { signal });
}

export async function buyNameColor(colorId) {
  return v2("/customization/name-colors/buy", {
    method: "POST",
    body: { color_id: colorId },
  });
}

export async function activateNameColor(colorId) {
  return v2("/customization/name-colors/activate", {
    method: "POST",
    body: { color_id: colorId },
  });
}

/* ── уведомления ──────────────────────────────────────────────────── */

export async function getNotifications(signal) {
  return v2("/engagement/notifications", { signal });
}

export async function getUnreadCount(signal) {
  const d = await v2("/engagement/notifications/unread-count", { signal });
  return typeof d?.count === "number" ? d.count : 0;
}

export async function markAllRead() {
  return v2("/engagement/notifications/read-all", { method: "POST" });
}

/* ── друзья ───────────────────────────────────────────────────────── */

/**
 * Списки друзей: {friends, incoming, outgoing}.
 * v1 отдавал их тремя разными запросами — здесь один.
 */
export async function getFriends(signal) {
  return v2("/social/friends", { signal });
}

export async function friendRequest(userId) {
  return v2(`/social/friends/request`, {
    method: "POST",
    body: { user_id: String(userId) },
  });
}

export async function friendAccept(userId) {
  return v2(`/social/friends/accept`, {
    method: "POST",
    body: { user_id: String(userId) },
  });
}

export async function friendRemove(userId) {
  return v2(`/social/friends/remove`, {
    method: "POST",
    body: { user_id: String(userId) },
  });
}

/** Кто сейчас онлайн. */
export async function getOnline(signal) {
  return v2("/social/online", { signal });
}

/* ── рейтинг ──────────────────────────────────────────────────────── */

/**
 * Текущий сезон: {code, title, ends_at, my_points, my_rank, leaders[]}.
 * Заменяет battle-rating/current из v1 (в v2 модель сезонов другая).
 */
export async function getRatingSeason(signal) {
  return v2("/engagement/rating/season", { signal });
}

/* ── кошелёк: расширение ──────────────────────────────────────────── */

/** История операций — общая для алмазов, звёзд и рейтинга. */
export async function getWalletHistory(signal) {
  return v2("/wallet/history", { signal });
}

/** Баланс звёзд (донатная валюта экосистемы). */
export async function getStars(signal) {
  const b = await getBalances(signal);
  return typeof b?.star === "number" ? b.star : 0;
}

/** Рейтинг из кошелька — в v2 он хранится там же, третьей валютой. */
export async function getRating(signal) {
  const b = await getBalances(signal);
  return typeof b?.rating === "number" ? b.rating : 0;
}
